import React, { useState } from "react";
import { X, AlertTriangle, Gauge, Activity, ArrowRight, FileText, Wrench, Camera, ChevronLeft, ChevronRight } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { API } from "../App";

// Helper to construct full image URL
const getImageUrl = (url) => {
  if (!url) return '';
  // If it's an API path (uploaded image), construct full URL
  if (url.startsWith('/api')) {
    return `${API.replace('/api', '')}${url}`;
  }
  // Otherwise it's an external URL, use as-is
  return url;
};

/**
 * ImageCarousel - Instagram-style multi-image carousel with dots navigation
 */
const ImageCarousel = ({ images, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const goToPrevious = (e) => {
    e.stopPropagation();
    setCurrentIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
  };
  
  const goToNext = (e) => {
    e.stopPropagation();
    setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
  };
  
  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  if (!images || images.length === 0) {
    return (
      <div className="h-56 bg-slate-50 border-2 border-dashed border-slate-200 rounded-lg flex flex-col items-center justify-center">
        <Camera className="w-10 h-10 text-slate-300 mb-2" />
        <p className="text-sm text-slate-400">No images configured</p>
        <p className="text-xs text-slate-300">Add via Asset Configuration</p>
      </div>
    );
  }

  return (
    <div className="relative group">
      {/* Main Image */}
      <div className="h-56 bg-slate-100 overflow-hidden rounded-lg">
        <img 
          src={getImageUrl(images[currentIndex].url)}
          alt={images[currentIndex].caption || title}
          className="w-full h-full object-cover transition-opacity duration-300"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      </div>
      
      {/* Navigation Arrows - Only show if multiple images */}
      {images.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-1 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronLeft className="w-4 h-4 text-slate-700" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-1 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronRight className="w-4 h-4 text-slate-700" />
          </button>
        </>
      )}
      
      {/* Dot Indicators - Only show if multiple images */}
      {images.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-1.5 h-1.5 rounded-full transition-all ${
                index === currentIndex 
                  ? 'bg-white w-3' 
                  : 'bg-white/60 hover:bg-white/80'
              }`}
            />
          ))}
        </div>
      )}
      
      {/* Image Counter */}
      {images.length > 1 && (
        <div className="absolute top-2 right-2 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded-full">
          {currentIndex + 1}/{images.length}
        </div>
      )}
    </div>
  );
};

/**
 * DetailSlidePanel - Horizontal PPT-style slide panel for item details
 * Compact layout with Instagram-style image carousel
 */
const DetailSlidePanel = ({ 
  isOpen, 
  onClose, 
  item, 
  itemType, 
  componentName,
  componentColor,
  relatedItems,
  onSelectRelated 
}) => {
  if (!isOpen || !item) return null;

  const getTypeConfig = () => {
    switch (itemType) {
      case "failure_mode":
        return {
          icon: AlertTriangle,
          color: "text-red-600",
          bgColor: "bg-red-50",
          borderColor: "border-red-300",
          label: "Failure Mode",
          badgeVariant: "destructive"
        };
      case "test":
        return {
          icon: Gauge,
          color: "text-blue-600",
          bgColor: "bg-blue-50",
          borderColor: "border-blue-300",
          label: "Diagnostic Test",
          badgeVariant: "default"
        };
      case "monitoring":
        return {
          icon: Activity,
          color: "text-green-600",
          bgColor: "bg-green-50",
          borderColor: "border-green-300",
          label: "Continuous Monitoring",
          badgeVariant: "secondary"
        };
      default:
        return {
          icon: AlertTriangle,
          color: "text-slate-600",
          bgColor: "bg-slate-50",
          borderColor: "border-slate-300",
          label: "Item",
          badgeVariant: "outline"
        };
    }
  };

  const config = getTypeConfig();
  const Icon = config.icon;

  // Use item images if available, otherwise empty array (no placeholders)
  const images = item.images && item.images.length > 0 
    ? item.images 
    : [];

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-[60] transition-opacity"
        onClick={onClose}
      />
      
      {/* Slide Panel - positioned with more height */}
      <div className="fixed inset-x-4 bottom-6 top-24 z-[70] transform transition-transform duration-300 ease-out flex items-end">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-7xl mx-auto w-full max-h-full flex flex-col">
          
          {/* Compact Header - Single Line */}
          <div className={`${config.bgColor} border-b ${config.borderColor} px-6 py-3 flex-shrink-0`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2.5 rounded-lg bg-white shadow-sm flex-shrink-0`}>
                  <Icon className={`w-6 h-6 ${config.color}`} strokeWidth={1.5} />
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h2 className="text-xl font-bold text-slate-900">{item.name}</h2>
                  <div className="flex items-center gap-2">
                    <Badge variant={config.badgeVariant} className="text-xs">
                      {config.label}
                    </Badge>
                    {componentName && (
                      <Badge variant="outline" className="text-xs bg-white">
                        {componentName}
                      </Badge>
                    )}
                    {item.severity && (
                      <Badge variant={item.severity === "Critical" ? "destructive" : "secondary"} className="text-xs">
                        {item.severity}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onClose}
                className="hover:bg-white/50 rounded-full h-9 w-9 p-0 flex-shrink-0"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Main Content - Larger 4-column grid with scroll */}
          <div className="p-5 flex-1 overflow-y-auto">
            <div className="grid grid-cols-12 gap-5">
              
              {/* Column 1 - Overview & Why It Matters (3 cols) */}
              <div className="col-span-3 space-y-4">
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2 text-sm">
                    <FileText className="w-4 h-4 text-slate-500" />
                    Overview
                  </h4>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {item.description || "Detailed description available in configuration."}
                  </p>
                </div>

                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2 text-sm">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    Why It Matters
                  </h4>
                  <p className="text-sm text-amber-700 leading-relaxed">
                    {item.importance || "Understanding importance helps prioritize diagnostics."}
                  </p>
                </div>
              </div>

              {/* Column 2 - Image Carousel (3 cols) */}
              <div className="col-span-3">
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden h-full flex flex-col">
                  <div className="bg-slate-100 px-4 py-2 border-b border-slate-200">
                    <h4 className="font-semibold text-slate-700 flex items-center gap-2 text-sm">
                      <Camera className="w-4 h-4 text-slate-500" />
                      {itemType === "failure_mode" ? "Failure Visualization" : itemType === "test" ? "Test Equipment" : "Monitoring System"}
                      {images.length > 1 && (
                        <span className="text-slate-400 font-normal">({images.length} images)</span>
                      )}
                    </h4>
                  </div>
                  
                  {/* Image Carousel */}
                  <div className="flex-1 p-2">
                    <ImageCarousel images={images} title={item.name} />
                  </div>
                  
                  <div className="px-4 py-2 bg-slate-50 border-t border-slate-200">
                    <p className="text-xs text-slate-400 italic">
                      {item.images ? "Custom images" : "Swipe or click arrows • Configurable in Asset Configuration"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Column 3 - Approach/Procedure (3 cols) */}
              <div className="col-span-3">
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 h-full">
                  <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2 text-sm">
                    <Wrench className="w-4 h-4 text-blue-500" />
                    {itemType === "test" ? "Testing Procedure" : itemType === "monitoring" ? "How It Works" : "Detection Approach"}
                  </h4>
                  <p className="text-sm text-blue-700 leading-relaxed">
                    {item.approach || "Standard operating procedures and methodologies will be detailed here once configured."}
                  </p>
                  
                  {/* Cost/Complexity indicators */}
                  {(item.cost || item.complexity) && (
                    <div className="flex gap-2 mt-4 pt-3 border-t border-blue-200">
                      {item.cost && (
                        <Badge variant="outline" className="text-xs bg-white">
                          Cost: {item.cost}
                        </Badge>
                      )}
                      {item.complexity && (
                        <Badge variant="outline" className="text-xs bg-white">
                          {item.complexity}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Column 4 - Related Items (3 cols) */}
              <div className="col-span-3">
                {relatedItems && (relatedItems.tests?.length > 0 || relatedItems.monitoring?.length > 0 || relatedItems.failureModes?.length > 0) ? (
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200 h-full">
                    <h4 className="font-semibold text-purple-800 mb-3 flex items-center gap-2 text-sm">
                      <ArrowRight className="w-4 h-4 text-purple-500" />
                      Related Items
                    </h4>
                    
                    <div className="space-y-3 max-h-56 overflow-y-auto">
                      {/* Related Tests */}
                      {relatedItems.tests?.length > 0 && (
                        <div>
                          <p className="text-xs text-purple-600 uppercase tracking-wide mb-1.5 font-medium">Tests</p>
                          <div className="space-y-1.5">
                            {relatedItems.tests.slice(0, 4).map((test, idx) => (
                              <button
                                key={idx}
                                onClick={() => onSelectRelated && onSelectRelated("test", test)}
                                className="w-full text-left px-3 py-1.5 rounded bg-white border border-blue-100 text-sm text-slate-700 hover:bg-blue-50 hover:border-blue-300 transition-all flex items-center gap-2 group"
                              >
                                <Gauge className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                                <span className="truncate">{test}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Related Monitoring */}
                      {relatedItems.monitoring?.length > 0 && (
                        <div>
                          <p className="text-xs text-purple-600 uppercase tracking-wide mb-1.5 font-medium">Monitoring</p>
                          <div className="space-y-1.5">
                            {relatedItems.monitoring.slice(0, 4).map((monitor, idx) => (
                              <button
                                key={idx}
                                onClick={() => onSelectRelated && onSelectRelated("monitoring", monitor)}
                                className="w-full text-left px-3 py-1.5 rounded bg-white border border-green-100 text-sm text-slate-700 hover:bg-green-50 hover:border-green-300 transition-all flex items-center gap-2 group"
                              >
                                <Activity className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                                <span className="truncate">{monitor}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Related Failure Modes */}
                      {relatedItems.failureModes?.length > 0 && (
                        <div>
                          <p className="text-xs text-purple-600 uppercase tracking-wide mb-1.5 font-medium">Detects</p>
                          <div className="space-y-1.5">
                            {relatedItems.failureModes.slice(0, 4).map((mode, idx) => (
                              <button
                                key={idx}
                                onClick={() => onSelectRelated && onSelectRelated("failure_mode", mode)}
                                className="w-full text-left px-3 py-1.5 rounded bg-white border border-red-100 text-sm text-slate-700 hover:bg-red-50 hover:border-red-300 transition-all flex items-center gap-2 group"
                              >
                                <AlertTriangle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                                <span className="truncate">{mode}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 h-full flex items-center justify-center">
                    <p className="text-sm text-slate-400 text-center">
                      No related items configured.<br/>
                      Use Asset Configuration to set relationships.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DetailSlidePanel;
